import type { ReactNode } from "react";

import type { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faCircleQuestion, faMagnifyingGlass } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Stack, Typography, styled } from "@mui/material";

import { Spinner } from "shared/atoms/Spinner";
import { Tooltip } from "shared/atoms/Tooltip";
import { type Breadcrumb, ViewBreadcrumbs } from "shared/containers/View/components/ViewBreadcrumbs";
import { DARK_BLUE } from "theme/colors";

type ViewProps = {
  actionButtons?: ReactNode[];
  additionalBreadcrumbs?: Breadcrumb[];
  children: ReactNode;
  className?: string;
  description?: string;
  fullScreen?: boolean;
  headerContent?: ReactNode;
  hideHeader?: boolean;
  hideBreadcrumbs?: boolean;
  info?: {
    content: ReactNode;
    icon?: IconProp;
  };
  isLoading?: boolean;
  removePadding?: boolean;
  title?: string;
};

// TODO: Add OOO indicator
export const View = ({
  actionButtons,
  additionalBreadcrumbs,
  children,
  className,
  description,
  fullScreen = true,
  headerContent,
  hideHeader,
  hideBreadcrumbs,
  info,
  isLoading,
  removePadding,
  title,
}: ViewProps) => {
  return (
    <ViewContainer
      spacing={3}
      p={removePadding ? 0 : { xs: 1.5, md: 2 }}
      className={className}
      minHeight={fullScreen ? "100vh" : "auto"}
    >
      {isLoading && (
        <Box py={4}>
          <Spinner />
        </Box>
      )}

      {!hideHeader && !isLoading && (
        <Content>
          {!hideBreadcrumbs && <ViewBreadcrumbs additionalBreadcrumbs={additionalBreadcrumbs} />}

          <NavigationContainer mt={!hideBreadcrumbs ? 1 : 0}>
            <Stack direction="row" alignItems="center" spacing={1}>
              {title && (
                <Typography variant="h1" fontWeight={700}>
                  {title}
                </Typography>
              )}
              {description && (
                <Tooltip title={description} placement="bottom" arrow>
                  <FontAwesomeIcon icon={faCircleQuestion} style={{ color: DARK_BLUE[60], fontSize: 16 }} />
                </Tooltip>
              )}
              {info?.content && (
                <Tooltip title={info.content} placement="bottom" arrow>
                  <FontAwesomeIcon
                    icon={info?.icon || faMagnifyingGlass}
                    style={{ color: DARK_BLUE[60], fontSize: 16 }}
                  />
                </Tooltip>
              )}
            </Stack>

            {headerContent && (
              <Stack direction="row" justifyContent="flex-start" flexGrow={2} flexWrap="wrap">
                {headerContent}
              </Stack>
            )}

            <Stack
              direction="row"
              justifyContent={{ xs: "flex-start", md: "flex-end" }}
              alignItems="center"
              spacing={1}
            >
              {actionButtons?.map((button, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <Box key={`action-button-${index}`}>{button}</Box>
              ))}
            </Stack>
          </NavigationContainer>
        </Content>
      )}

      {!isLoading && <Content className="view-content-container">{children}</Content>}
    </ViewContainer>
  );
};

const ViewContainer = styled(Stack)({
  width: "100%",
  boxSizing: "border-box", // TODO: Make default in theme
});

const Content = styled(Box)({
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,
  boxSizing: "border-box",
});

const NavigationContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "center",
  flexWrap: "wrap",
  gap: theme.spacing(4),
  [theme.breakpoints.down("md")]: {
    gap: theme.spacing(2),
  },
}));
